import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { blogPosts } from '../../data/blog-posts';

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const article = blogPosts.find(p => p.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | reputrust.ai`;

      // Add Schema.org JSON-LD for Article
      const schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "image": article.image,
        "datePublished": article.date,
        "dateModified": article.date,
        "author": {
          "@type": "Organization",
          "name": article.author
        },
        "publisher": {
          "@type": "Organization",
          "name": "SMC Simon Müller Consulting e.K.",
          "url": (import.meta as any).env.VITE_SITE_URL || "https://reputrust.ai"
        },
        "description": article.content.substring(0, 160).replace(/<[^>]*>/g, ''),
        "url": `${(import.meta as any).env.VITE_SITE_URL || "https://reputrust.ai"}/blog/${article.slug}`
      });
      document.head.appendChild(schemaScript);

      window.scrollTo(0, 0);

      return () => {
        if (schemaScript.parentNode) {
          schemaScript.parentNode.removeChild(schemaScript);
        }
      };
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-600 selection:bg-tron-primary/30 selection:text-[#0F172A] flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center pt-20 pb-20 text-center relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-200/20 rounded-full blur-[100px] animate-pulse"></div>
          </div>

          <div className="bg-white p-12 rounded-2xl max-w-md mx-auto relative z-10 shadow-lg border border-slate-200">
            <h1 className="text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Inter Tight, sans-serif' }}>Artikel nicht gefunden</h1>
            <p className="text-slate-600 mb-8" style={{ fontFamily: 'DM Sans, sans-serif' }}>Der angeforderte Artikel konnte nicht gefunden werden.</p>
            <Link to="/blog" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] border border-blue-200 text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] px-6 py-3 rounded-lg hover:bg-blue-100 transition-all duration-300 font-medium uppercase tracking-wider">
              Zurück zum Blog
              <i className="ri-arrow-left-line"></i>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-600 selection:bg-tron-primary/30 selection:text-[#0F172A]">
      <Header />

      <div className="pt-20 pb-12 relative">
        {/* Article Header */}
        <div className="relative h-[400px] md:h-[500px] overflow-hidden group">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-center scale-105 group-hover:scale-110 transition-transform duration-[2s]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-50 via-slate-50/80 to-transparent"></div>

          {/* Animated Overlay */}
          <div className="absolute inset-0 bg-[url('/images/grid-pattern.png')] opacity-5 mix-blend-overlay"></div>

          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 z-10">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white/90 border border-slate-200 text-slate-700 px-3 py-1 rounded-full text-sm font-medium mb-6 backdrop-blur-md shadow-sm">
                {article.category}
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 leading-tight" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-6 text-slate-600 text-sm md:text-base font-orbitron tracking-wide">
                <span className="flex items-center gap-2">
                  <i className="ri-calendar-line text-white"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-time-line text-white"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-user-line text-white"></i>
                  {article.author}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 relative z-10">
          {/* Background Elements */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full overflow-hidden pointer-events-none -z-10">
            <div className="absolute top-40 -left-20 w-96 h-96 bg-tron-primary/5 rounded-full blur-[100px]"></div>
            <div className="absolute bottom-40 -right-20 w-96 h-96 bg-tron-secondary/5 rounded-full blur-[100px]"></div>
          </div>

          <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-slate-200">
            {/* Social Share Buttons */}
            <div className="flex items-center gap-3 mb-10 pb-8 border-b border-slate-200">
              <span className="text-slate-500 font-medium font-orbitron uppercase tracking-wider text-sm">Share Protocol:</span>
              <button className="w-10 h-10 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg flex items-center justify-center hover:bg-[#1877F2] hover:border-[#1877F2] hover:shadow-sm hover:text-white transition-all duration-300">
                <i className="ri-facebook-fill"></i>
              </button>
              <button className="w-10 h-10 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg flex items-center justify-center hover:bg-[#1DA1F2] hover:border-[#1DA1F2] hover:shadow-sm hover:text-white transition-all duration-300">
                <i className="ri-twitter-x-fill"></i>
              </button>
              <button className="w-10 h-10 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg flex items-center justify-center hover:bg-[#0A66C2] hover:border-[#0A66C2] hover:shadow-sm hover:text-white transition-all duration-300">
                <i className="ri-linkedin-fill"></i>
              </button>
              <button className="w-10 h-10 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg flex items-center justify-center hover:bg-[#25D366] hover:border-[#25D366] hover:shadow-sm hover:text-white transition-all duration-300">
                <i className="ri-whatsapp-fill"></i>
              </button>
              <button
                className="w-10 h-10 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg flex items-center justify-center hover:bg-tron-primary hover:border-tron-primary hover:text-white hover:shadow-sm transition-all duration-300 ml-auto"
                onClick={() => navigator.clipboard?.writeText(window.location.href)}
              >
                <i className="ri-link"></i>
              </button>
            </div>

            {/* Article Body */}
            <div
              className="prose prose-slate prose-lg max-w-none
                  prose-headings:font-bold prose-headings:text-[#0F172A] prose-headings:font-inter-tight
                  prose-h2:text-3xl prose-h2:mt-16 prose-h2:mb-8 prose-h2:border-l-4 prose-h2:border-tron-primary prose-h2:pl-6
                  prose-h3:text-2xl prose-h3:mt-12 prose-h3:mb-6 prose-h3:text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]
                  prose-p:text-slate-600 prose-p:leading-relaxed prose-p:mb-6
                  prose-ul:my-6 prose-ul:space-y-3 prose-ul:pl-6
                  prose-li:text-slate-600 prose-li:marker:text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]
                  prose-strong:text-[#0F172A] prose-strong:font-bold
                  prose-a:text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] prose-a:no-underline hover:prose-a:text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] hover:prose-a:underline transition-all
                  prose-blockquote:border-l-4 prose-blockquote:border-emerald-400 prose-blockquote:bg-slate-50 prose-blockquote:p-6 prose-blockquote:rounded-r-xl prose-blockquote:text-slate-700 prose-blockquote:italic prose-blockquote:not-italic
                  prose-code:bg-slate-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] prose-code:font-mono prose-code:border prose-code:border-slate-200
                  prose-pre:bg-[#0F172A] prose-pre:text-slate-50 prose-pre:border prose-pre:border-slate-800 prose-pre:p-6 prose-pre:rounded-xl prose-pre:shadow-inner
                  prose-img:rounded-xl prose-img:shadow-md prose-img:border prose-img:border-slate-200"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Back to Blog */}
            <div className="mt-16 pt-8 border-t border-slate-200 flex justify-between items-center">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors font-medium hover:translate-x-[-4px] transform duration-300"
              >
                <i className="ri-arrow-left-line"></i>
                Zurück zum Blog
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-20">
            <h3 className="text-3xl font-bold text-[#0F172A] mb-8 border-l-4 border-tron-primary pl-6" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
              Verwandte Artikel
            </h3>
            <div className="grid sm:grid-cols-2 gap-8">
              {blogPosts
                .filter((p) => p.slug !== article.slug)
                .slice(0, 2)
                .map((relatedArticle) => (
                  <Link
                    key={relatedArticle.id}
                    to={`/blog/${relatedArticle.slug}`}
                    className="group block bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-blue-300 transition-all duration-300 hover:shadow-lg shadow-sm"
                  >
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={relatedArticle.image}
                        alt={relatedArticle.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-60 group-hover:opacity-40 transition-opacity"></div>
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur border border-slate-200 text-slate-700 px-3 py-1 rounded-full text-xs font-medium font-orbitron shadow-sm">
                          {relatedArticle.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h4 className="text-xl font-bold text-[#0F172A] group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] transition-colors mb-2 line-clamp-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                        {relatedArticle.title}
                      </h4>
                      <div className="flex items-center gap-4 text-sm text-slate-500 font-orbitron mt-4">
                        <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {relatedArticle.date}</span>
                        <span className="flex items-center gap-1"><i className="ri-time-line"></i> {relatedArticle.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}